import { defineStore } from 'pinia';
import _ from 'lodash';
import Vue from 'vue';

import FileApi from '@/store/api/cms/file';

const defaultForm = {};
const defaultRules = {};
const defaultUi = {};

export const useFileManagerStore = defineStore('fileManager', {
    state: () => ({
        loading: true,
        filesPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                // Lives in the store, not in a view, so the table and the tile view drive
                // ONE selection: switching between them mid-selection must not lose it.
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            tag: {
                loading: false,
                items: []
            },
            visibility: {
                loading: false,
                items: [
                    { value: 'public', label: 'Public' },
                    { value: 'private', label: 'Private' }
                ]
            }
        },
        form: _.cloneDeep(defaultForm),
        rules: _.cloneDeep(defaultRules),
        ui: _.cloneDeep(defaultUi)
    }),

    getters: {},

    actions: {
        addPaginatedFilesToState(data) {
            // Keep the in-flight selection: the response's pageInfo carries the server's
            // echo of it, but a plain cloneDeep would also drop anything ticked while the
            // request was on the wire.
            const selection = _.get(this.filesPaginated, 'pageInfo.selection');

            this.filesPaginated.items = data.items;
            this.filesPaginated.pageInfo = _.cloneDeep(data.pageInfo);

            if (selection && !_.get(data, 'pageInfo.selection')) {
                Vue.set(this.filesPaginated.pageInfo, 'selection', selection);
            }
            this.filesPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.filesPaginated.link = _.cloneDeep(data.config.link);
            this.filesPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.filesPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.filesPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.filesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.filesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.filesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.filesPaginated.filters, key);
            } else {
                Vue.set(this.filesPaginated.filters, key, value);
            }
        },

        definePathValue(value) {
            Vue.set(this.filesPaginated, 'path', value);
        },

        defineSorting({ key, value }) {
            this.filesPaginated.variables.order = key;
            this.filesPaginated.variables.direction = value;
        },

        // Bulk selection. Same contract as every other paginated list (see
        // stores/shopProduct.js): 'exclusive' means "everything matching the current
        // filters EXCEPT these ids", which is what makes "select all 3584" one request
        // instead of 3584 identifiers.
        paginationBulkSelectionCheckAll({ value }) {
            const selection = this.filesPaginated.pageInfo.selection;

            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.filesPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', 0);
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = _.get(this.filesPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));
            const selection = this.filesPaginated.pageInfo.selection;

            Vue.set(selection, 'type', 'inclusive');

            if (value === true) {
                Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
            } else {
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            const selection = this.filesPaginated.pageInfo.selection;
            const id = parseInt(item.id);
            const listed = selection.identifiers.map(el => parseInt(el)).indexOf(id) >= 0;

            // In exclusive mode the identifiers are the EXCEPTIONS, so ticking a box
            // removes an id and unticking adds one - the opposite of inclusive mode.
            const checked = selection.type === 'exclusive' ? !listed : listed;
            const push = selection.type === 'exclusive' ? checked === true : checked === false;

            if (push === true) {
                Vue.set(selection.identifiers, selection.identifiers.length, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            Vue.set(selection, 'total', checked === false ? selection.total + 1 : selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.filesPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        defineFilterVisibility(value) {
            Vue.set(this.filesPaginated, 'showFilters', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateTagOptionsLoading(value) {
            Vue.set(this.options.tag, 'loading', value);
        },

        insertTagOptions(tags) {
            Vue.set(this.options.tag, 'items', tags);
        },

        // Async actions
        fetchFilesPaginated(payload = {}) {
            return FileApi.fetchFilesPaginated({ ...payload, ...this.filesPaginated }, { full: true }).then(data => {
                this.addPaginatedFilesToState(data);
                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        checkIfFileExists({ name, path, cmsID }) {
            return FileApi.validate(name, path, cmsID);
        },

        addFile(file) {
            return FileApi.create(file);
        },

        replaceFile(file) {
            return FileApi.update(file);
        },

        deleteFile(id) {
            return FileApi.delete(id);
        },

        fetchTagOptions(cmsID) {
            this.updateTagOptionsLoading(true);
            return FileApi.fetchTagList(cmsID)
                .then(tags => {
                    this.updateTagOptionsLoading(false);
                    this.insertTagOptions(tags);
                })
                .catch(err => {
                    this.updateTagOptionsLoading(false);
                    throw err;
                });
        }
    }
});
